// src/redux/slices/userSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../axiosinstance";

interface UserState {
  id: string | null;
  full_name: string | null;
  email: string | null;
  membership_type: string | null;
  role: string | null;
  avatar: string | null;
  is_verified_email: boolean;
  // Admin dashboard modules this admin may open; null = not sent (full access)
  modules: string[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  id: null,
  full_name: null,
  email: null,
  membership_type: null,
  role: null,
  avatar: null,
  is_verified_email: false,
  modules: null,
  loading: false,
  error: null,
};

// Fetch user profile
export const fetchUserProfile = createAsyncThunk(
  "user/fetchUserProfile",
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get("/auth/me");
      const user = response.data.data;
      return {
        id: user.id,
        full_name: user.full_name,
        email: user.email,
        membership_type: user.membership_type,
        role: user.role,
        avatar: user.avatar,
        is_verified_email: user.is_verified_email,
        modules: Array.isArray(user.modules) ? user.modules : null,
      };
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch user profile"
      );
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUser(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUserProfile.fulfilled,
        (state, action: PayloadAction<Partial<UserState>>) => {
          state.loading = false;
          Object.assign(state, action.payload);
        }
      )
      .addCase(
        fetchUserProfile.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string | null;
        }
      );
  },
});

export const { clearUser } = userSlice.actions;

export default userSlice.reducer;
